import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Tab, Tabs, TextField } from '@mui/material';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridColumns as XGridColumns,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { usePageVisitTimeTracker } from 'app-insights/appInsightsTracking';
import { makeStyles } from 'tss-react/mui';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { useTranslation } from 'react-i18next';
import { TypeAheadDropdown } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/TypeAheadDropdown';
import { ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { ChipsContainer } from 'app/ComponentLibrary/Filter/ChipsContainer';
import { getMultiSelectOptions } from 'app/components/GlobalSearch/helper';
import { CandidateService } from 'app/services/CandidateServices/candidate-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { convertToFormat } from 'app/helpers/dateHelper';
import { cloneDeep, isEqual, merge } from 'lodash';
import {
  formatEngagementGridCell,
  formatIsoToUsDate,
  IInquiryRecord,
} from 'app/components/Candidate/CandidateEngagement/candidateLeadsUtils';
import {
  CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY,
  getCandidateProfileLeadsGridTag,
} from 'app/components/Candidate/CandidateEngagement/candidateInquiryGridSurfaceIds';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'types';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectColumnVisibilityModel } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { trackException } from 'app-insights/appInsightsTracking';
import { getSessionValue, SessionKey, setSessionValue, StorageType } from 'utils/customHooks/sessionStorage/sessionHelpers';

interface IBrandOption {
  id?: number;
  name?: string;
  value?: string;
}

interface ILeadsFilterSnapshot {
  activeTab: (typeof tabKeys)[number];
  brandFilter: ISelection[];
  createdDateFilter: { startDateIso?: string; endDateIso?: string } | null;
  leadOrigin: string;
}

const tabKeys = ['inquiry', 'engagement', 'referral', 'application', 'all'] as const;

const useStyles = makeStyles()(() => ({
  filtersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) auto',
    gap: 8,
    alignItems: 'end',
    padding: '12px 12px 0 12px',
    marginBottom: 4,
  },
  filterCell: {
    minWidth: 0,
  },
  leadOriginField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: 36.7,
      fontSize: 12,
      fontWeight: 400,
      borderRadius: 4,
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 10px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
  filterActionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
    justifySelf: 'end',
  },
  chipsRow: {
    display: 'block',
    padding: '4px 12px 0',
    marginBottom: 10,
  },
  gridSection: {
    marginTop: 12,
    border: '1px solid #ececec',
    borderRadius: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 0 0 1px #ececec inset',
  },
  tableWrap: {
    padding: '0 12px 12px 12px',
  },
  tabRoot: {
    marginTop: 4,
    minHeight: 38,
  },
  tabCell: {
    minHeight: 38,
    textTransform: 'none',
    fontSize: 12,
    paddingLeft: 14,
    paddingRight: 14,
  },
}));

export const CandidateLeadsWrapper = () => {
  usePageVisitTimeTracker('Candidate Details:Leads Tab');
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { travelerId: candidateId } = useParams<{ travelerId: string; brandId: string }>();
  const candidateService = useMemo(() => CandidateService.getInstance(), []);

  const leadsGridTag = useMemo(() => getCandidateProfileLeadsGridTag(candidateId ?? ''), [candidateId]);

  const gridPreferences = useSelector((state: RootState) =>
    state.userPreferenceData?.userGridPreferences?.find(item => item.id === leadsGridTag),
  );
  const userPreference = useSelector(selectUserPreference);
  const columnVisibilityModel = useSelector(selectColumnVisibilityModel);
  const gridApiRef = useGridApiRef();

  const routeTravelerId = Number(candidateId);

  const [activeTab, setActiveTab] = useState<(typeof tabKeys)[number]>('inquiry');
  const [brandFilterDraft, setBrandFilterDraft] = useState<ISelection[]>([]);
  const [brandFilterApplied, setBrandFilterApplied] = useState<ISelection[]>([]);
  const [createdDateFilter, setCreatedDateFilter] = useState<{
    startDate?: Date;
    endDate?: Date;
  } | null>(null);
  const [leadOriginInput, setLeadOriginInput] = useState('');
  const [leadOriginApplied, setLeadOriginApplied] = useState('');
  const commitLeadOriginFilter = React.useCallback(() => {
    setLeadOriginApplied(leadOriginInput.trim());
  }, [leadOriginInput]);

  const [brandOptions, setBrandOptions] = useState<IBrandOption[]>([]);
  const [apiRows, setApiRows] = useState<IInquiryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [sortColumn, setSortColumn] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const filtersHydratedRef = React.useRef(false);
  const skipNextPersistRef = React.useRef(false);

  useEffect(() => {
    if (!userPreference?.loaded) {
      return;
    }

    filtersHydratedRef.current = false;
    skipNextPersistRef.current = true;

    const applyLeadsFilterSnapshot = (snapshot: ILeadsFilterSnapshot) => {
      if (snapshot.activeTab && tabKeys.includes(snapshot.activeTab)) {
        setActiveTab(snapshot.activeTab);
      } else {
        setActiveTab('inquiry');
      }

      const restoredBrand = Array.isArray(snapshot.brandFilter) ? snapshot.brandFilter : [];
      const restoredDates =
        snapshot.createdDateFilter?.startDateIso || snapshot.createdDateFilter?.endDateIso
          ? {
              startDate: snapshot.createdDateFilter.startDateIso
                ? new Date(snapshot.createdDateFilter.startDateIso)
                : undefined,
              endDate: snapshot.createdDateFilter.endDateIso
                ? new Date(snapshot.createdDateFilter.endDateIso)
                : undefined,
            }
          : null;
      setBrandFilterDraft(restoredBrand);
      setBrandFilterApplied(restoredBrand);
      setCreatedDateFilter(restoredDates);
      setLeadOriginInput(snapshot.leadOrigin ?? '');
      setLeadOriginApplied(snapshot.leadOrigin ?? '');
    };

    const allSnapshots =
      (getSessionValue(SessionKey.candidateProfileLeadsSessionFilters, StorageType.sessionStorage) as
        | Record<string, ILeadsFilterSnapshot>
        | undefined) ?? {};
    const snapshot = allSnapshots[leadsGridTag];
    if (snapshot) {
      applyLeadsFilterSnapshot(snapshot);
      filtersHydratedRef.current = true;
      return;
    }

    const durable = gridPreferences?.value?.[CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY] as ILeadsFilterSnapshot | undefined;
    if (
      durable &&
      typeof durable === 'object' &&
      (!durable.activeTab || tabKeys.includes(durable.activeTab))
    ) {
      applyLeadsFilterSnapshot(durable);
      filtersHydratedRef.current = true;
      return;
    }

    setActiveTab('inquiry');
    setBrandFilterDraft([]);
    setBrandFilterApplied([]);
    setCreatedDateFilter(null);
    setLeadOriginInput('');
    setLeadOriginApplied('');
    filtersHydratedRef.current = true;
    // gridPreferences intentionally omitted from deps — durable snapshot updates must not re-run hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsGridTag, userPreference?.loaded]);

  useEffect(() => {
    if (!filtersHydratedRef.current || !userPreference?.loaded) {
      return;
    }
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    const nextSnapshot: ILeadsFilterSnapshot = {
      activeTab,
      brandFilter: brandFilterApplied,
      createdDateFilter:
        createdDateFilter?.startDate || createdDateFilter?.endDate
          ? {
              startDateIso: createdDateFilter?.startDate?.toISOString(),
              endDateIso: createdDateFilter?.endDate?.toISOString(),
            }
          : null,
      leadOrigin: leadOriginApplied,
    };

    const allSnapshots =
      (getSessionValue(SessionKey.candidateProfileLeadsSessionFilters, StorageType.sessionStorage) as
        | Record<string, ILeadsFilterSnapshot>
        | undefined) ?? {};
    setSessionValue(
      SessionKey.candidateProfileLeadsSessionFilters,
      {
        ...allSnapshots,
        [leadsGridTag]: nextSnapshot,
      },
      StorageType.sessionStorage,
    );

    const prevSnapshot = gridPreferences?.value?.[CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY] as
      | ILeadsFilterSnapshot
      | undefined;
    if (!isEqual(prevSnapshot, nextSnapshot)) {
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: leadsGridTag,
          value: {
            ...(gridPreferences?.value ?? {}),
            [CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY]: nextSnapshot,
          },
        }),
      );
    }
  }, [
    leadsGridTag,
    activeTab,
    brandFilterApplied,
    createdDateFilter,
    leadOriginApplied,
    userPreference?.loaded,
    dispatch,
    gridPreferences?.value,
  ]);

  useEffect(() => {
    let disposed = false;

    getSearchFilterOptions('brands', true).then(response => {
      if (disposed) {
        return;
      }
      const options = getMultiSelectOptions(response || [], { withoutAllOption: true }) as IBrandOption[];
      setBrandOptions(options);
    });

    return () => {
      disposed = true;
    };
  }, []);

  const brandDropdownOptions = useMemo<ISelection[]>(() => {
    if (brandOptions.length === 0) {
      return [];
    }
    return brandOptions
      .map(option => {
        const value = option.value || option.name || '';
        const label = option.name || option.value || '';
        return {
          id: option.id,
          name: label,
          value,
        };
      })
      .filter(item => item.value && item.name);
  }, [brandOptions]);

  useEffect(() => {
    let disposed = false;

    if (!routeTravelerId) {
      setIsLoading(false);
      setApiRows([]);
      return undefined;
    }

    setIsLoading(true);

    const brandIds = brandFilterApplied.map(b => Number(b.id || 0)).filter(Boolean);
    const recordTypes = activeTab !== 'all' ? [activeTab] : undefined;

    const params = {
      candidateIds: [routeTravelerId],
      brandIds: brandIds.length > 0 ? brandIds : undefined,
      recordTypes,
      createdDateMin: createdDateFilter?.startDate?.toISOString(),
      createdDateMax: createdDateFilter?.endDate?.toISOString(),
      leadOrigin: leadOriginApplied || undefined,
      sortColumn,
      sortDirection,
      pageNumber: 1,
      pageSize: 200,
    };

    candidateService
      .getEngagementInquiries(params)
      .then(response => {
        if (disposed) return;
        if (httpSuccess(response?.status)) {
          const data = response.data as {
            items: Array<{
              leadInquiryId: string;
              candidateId: string;
              candidate: { firstName: string; lastName: string };
              recordType: string;
              brandId?: string;
              brand: string;
              discipline: string;
              leadOrigin: string;
              leadSourceCategory: string;
              ownerName: string;
              externalInquiryId?: string;
              jobId?: number | null;
              orderId?: number | null;
              createdDate: string;
              lastUpdatedDate: string;
            }>;
          };
          const records = (data?.items ?? []).map(r => ({
            id: String(r.leadInquiryId),
            leadInquiryId: String(r.leadInquiryId ?? ''),
            candidateId: Number(r.candidateId),
            candidateName: `${r.candidate?.firstName ?? ''} ${r.candidate?.lastName ?? ''}`.trim(),
            recordType: (r.recordType ?? 'inquiry') as IInquiryRecord['recordType'],
            brandId: r.brandId != null && r.brandId !== '' ? String(r.brandId) : '',
            brand: r.brand ?? '',
            discipline: r.discipline ?? '',
            leadOrigin: r.leadOrigin ?? '',
            leadSourceCategory: r.leadSourceCategory ?? '',
            ownerName: r.ownerName ?? '',
            externalInquiryId: r.externalInquiryId ?? '',
            jobId: r.jobId ?? null,
            orderId: r.orderId ?? null,
            createdDate: formatIsoToUsDate(r.createdDate),
            lastUpdatedDate: formatIsoToUsDate(r.lastUpdatedDate),
          }));
          setApiRows(records);
        } else {
          setApiRows([]);
        }
      })
      .catch(() => {
        if (disposed) return;
        setApiRows([]);
      })
      .finally(() => {
        if (!disposed) {
          setIsLoading(false);
        }
      });

    return () => {
      disposed = true;
    };
  }, [
    activeTab,
    brandFilterApplied,
    createdDateFilter,
    leadOriginApplied,
    routeTravelerId,
    sortColumn,
    sortDirection,
    reloadToken,
    candidateService,
  ]);

  const baseColumns = useMemo<GridColDef<IInquiryRecord>[]>(
    () => [
      {
        field: 'leadInquiryId',
        headerName: t('candidateEngagement.columns.leadInquiryId'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'brandId',
        headerName: t('candidateEngagement.columns.brandId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'candidateName',
        headerName: t('candidateEngagement.columns.candidateName'),
        width: 160,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'candidateId',
        headerName: t('candidateEngagement.columns.candidateId'),
        width: 120,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'brand',
        headerName: t('candidateEngagement.columns.brand'),
        width: 140,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'discipline',
        headerName: t('candidateEngagement.columns.discipline'),
        width: 160,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'leadOrigin',
        headerName: t('candidateEngagement.columns.leadOrigin'),
        width: 150,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'leadSourceCategory',
        headerName: t('candidateEngagement.columns.leadSourceCategory'),
        width: 150,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'ownerName',
        headerName: t('candidateEngagement.columns.owner'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'externalInquiryId',
        headerName: t('candidateEngagement.columns.externalInquiryId'),
        width: 140,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'jobId',
        headerName: t('candidateEngagement.columns.jobId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'orderId',
        headerName: t('candidateEngagement.columns.orderId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'createdDate',
        headerName: t('candidateEngagement.columns.inquiryCreated'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'lastUpdatedDate',
        headerName: t('candidateEngagement.columns.lastUpdated'),
        minWidth: 130,
        flex: 1,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
    ],
    [t],
  );

  const defaultColumns = useMemo<GridColDef<IInquiryRecord>[]>(
    () => [
      {
        field: 'recordType',
        headerName: t('candidateEngagement.columns.recordType'),
        width: 120,
        valueFormatter: params => {
          const raw = String(params.value ?? '').trim();
          if (!raw) {
            return formatEngagementGridCell('');
          }
          return `${raw.charAt(0).toUpperCase()}${raw.slice(1)}`;
        },
      },
      ...baseColumns,
    ],
    [baseColumns, t],
  );

  const defaultColumnVisibilityModel = useMemo((): GridColumnVisibilityModel => {
    const model: GridColumnVisibilityModel = {};
    defaultColumns.forEach(col => {
      if (col.field) {
        model[col.field] = true;
      }
    });
    return model;
  }, [defaultColumns]);

  const [displayColumns, setDisplayColumns] = useState<GridColDef<IInquiryRecord>[]>([]);
  const [gridInitialState, setGridInitialState] = useState<
    { columns: { columnVisibilityModel: GridColumnVisibilityModel } } | undefined
  >();

  const getColumnPreferences = React.useCallback(
    (cols: GridColDef<IInquiryRecord>[]) => {
      try {
        const columnPreferences = gridPreferences?.value;
        const filterSetColumns = gridPreferences?.value?.filterAttributes?.filterSet?.find(
          item => item.name === gridPreferences?.value?.selectedFilter?.name,
        )?.columns;
        if (columnPreferences && (columnPreferences.columns || filterSetColumns)) {
          const newColumns = cols.map(column => {
            const foundIndex = (filterSetColumns ?? columnPreferences.columns).findIndex(
              item => item?.field === column?.field,
            );
            if (foundIndex !== -1) {
              return {
                ...column,
                ...(filterSetColumns ?? columnPreferences.columns)[foundIndex],
              };
            }
            return column;
          });
          return newColumns.sort((a, b) => {
            const filterSetColumnsIndexA = (filterSetColumns ?? columnPreferences.columns).findIndex(
              i => i.field === a.field,
            );
            const filterSetColumnsIndexB = (filterSetColumns ?? columnPreferences.columns).findIndex(
              i => i.field === b.field,
            );

            const indexA = filterSetColumnsIndexA === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexA;
            const indexB = filterSetColumnsIndexB === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexB;

            return indexA - indexB;
          });
        }
        return cols;
      } catch (err) {
        trackException({
          exception: err,
          properties: { columns: cols, grid: leadsGridTag, surface: 'CandidateProfileLeads' },
        });
        return cols;
      }
    },
    [gridPreferences, leadsGridTag],
  );

  const determineGridColumns = React.useCallback(
    (isDefault = false) => {
      const next = isDefault ? cloneDeep(defaultColumns) : getColumnPreferences(cloneDeep(defaultColumns));
      setDisplayColumns(next);
    },
    [defaultColumns, getColumnPreferences],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!displayColumns || displayColumns.length === 0 || !userPreference.loaded || defaultColumns.length === 0) {
        return;
      }
      const currentGridColumns = new GridColumns(gridColumns).columns;

      const i = (gridPreferences?.value?.filterAttributes?.filterSet || [])?.findIndex(
        item => item.name === gridPreferences?.value?.selectedFilter?.name,
      );

      if (currentGridColumns && currentGridColumns.length > 0 && currentGridColumns[0].field === '__check__') {
        currentGridColumns.shift();
      }

      const comparableColumns = new GridColumns(
        i !== -1 ? gridPreferences?.value?.filterAttributes?.filterSet[i]?.columns : gridPreferences?.value?.columns,
        i !== -1
          ? gridPreferences?.value?.filterAttributes?.filterSet[i]?.columnVisibilityModel
          : gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        type FilterSetRow = {
          name?: string;
          columns?: XGridColumns;
          columnVisibilityModel?: GridColumnVisibilityModel;
        };
        let newFilterSet: FilterSetRow[] | undefined;
        if (i !== -1) {
          newFilterSet = cloneDeep(gridPreferences?.value?.filterAttributes?.filterSet) as
            | FilterSetRow[]
            | undefined;
          if (newFilterSet?.[i]) {
            newFilterSet[i] = {
              ...newFilterSet[i],
              columns: currentGridColumns,
              columnVisibilityModel: visibilityModel,
            };
          }
        }

        const preferenceData = {
          id: leadsGridTag,
          value: {
            ...(gridPreferences?.value || {}),
            filterAttributes: {
              ...gridPreferences?.value?.filterAttributes,
              filterSet: newFilterSet ?? gridPreferences?.value?.filterAttributes?.filterSet,
            },
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };

        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayColumns, userPreference.loaded, defaultColumns.length, gridPreferences?.value, dispatch, leadsGridTag],
  );

  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(leadsGridTag));
    }
  }, [userPreference?.loaded, leadsGridTag, dispatch]);

  useEffect(() => {
    setGridInitialState(undefined);
  }, [leadsGridTag]);

  useEffect(() => {
    if (!userPreference?.loaded || defaultColumns.length === 0) {
      return;
    }
    setDisplayColumns(getColumnPreferences(cloneDeep(defaultColumns)));
  }, [
    userPreference?.loaded,
    leadsGridTag,
    defaultColumns,
    getColumnPreferences,
    gridPreferences?.value?.columns,
    gridPreferences?.value?.filterAttributes?.filterSet,
    gridPreferences?.value?.selectedFilter?.name,
  ]);

  const displayColumnsSignature = useMemo(() => JSON.stringify(displayColumns), [displayColumns]);

  useEffect(() => {
    if (!userPreference?.loaded || displayColumns.length === 0) {
      return;
    }
    const savedModel = gridPreferences?.value?.columnVisibilityModel;
    const resolvedVisibilityModel = savedModel || defaultColumnVisibilityModel;
    if (
      savedModel &&
      !Object.keys(defaultColumnVisibilityModel).every(key => Object.keys(savedModel).includes(key))
    ) {
      const cols = { ...defaultColumnVisibilityModel };
      merge(cols, savedModel);
      dispatch(gridStateActions.setColumnVisibilityModel(cols));
      determineGridColumns(false);
      setGridInitialState({ columns: { columnVisibilityModel: cols } });
    } else {
      dispatch(gridStateActions.setColumnVisibilityModel(resolvedVisibilityModel));
      setGridInitialState({ columns: { columnVisibilityModel: resolvedVisibilityModel } });
    }
  }, [
    displayColumns.length,
    displayColumnsSignature,
    gridPreferences?.value?.columnVisibilityModel,
    userPreference?.loaded,
    defaultColumnVisibilityModel,
    determineGridColumns,
    dispatch,
    leadsGridTag,
  ]);

  const tabTitle = useMemo(() => {
    const map: Record<(typeof tabKeys)[number], string> = {
      inquiry: t('candidateEngagement.tabs.inquiries'),
      engagement: t('candidateEngagement.tabs.engagementActivity'),
      referral: t('candidateEngagement.tabs.referrals'),
      application: t('candidateEngagement.tabs.applications'),
      all: t('candidateEngagement.tabs.unifiedOverview'),
    };
    return map[activeTab];
  }, [activeTab, t]);

  const tabs = useMemo(
    () => [
      { label: t('candidateEngagement.tabs.inquiries'), value: 'inquiry' as const },
      { label: t('candidateEngagement.tabs.engagementActivity'), value: 'engagement' as const },
      { label: t('candidateEngagement.tabs.referrals'), value: 'referral' as const },
      { label: t('candidateEngagement.tabs.applications'), value: 'application' as const },
      { label: t('candidateEngagement.tabs.unifiedOverview'), value: 'all' as const },
    ],
    [t],
  );

  const brandChips = useMemo(
    () => ({
      brand: brandFilterApplied.map(item => ({
        label: `${t('candidateEngagement.filters.brand')}: ${item.name || String(item.value || '')}`,
        value: item.value,
        enableDelete: true,
      })),
    }),
    [brandFilterApplied, t],
  );

  const leadOriginChips = useMemo(
    () =>
      leadOriginApplied
        ? [
            {
              label: `${t('candidateEngagement.filters.leadOrigin')}: ${leadOriginApplied}`,
              value: leadOriginApplied,
              enableDelete: true,
            },
          ]
        : [],
    [leadOriginApplied, t],
  );

  const createdDateChips = useMemo(() => {
    if (!createdDateFilter?.startDate && !createdDateFilter?.endDate) {
      return [];
    }
    const filterLabel = t('candidateEngagement.filters.inquiryCreatedDate');
    const startStr = createdDateFilter.startDate
      ? convertToFormat(createdDateFilter.startDate, 'MM/DD/YYYY') || ''
      : '';
    const endStr = createdDateFilter.endDate
      ? convertToFormat(createdDateFilter.endDate, 'MM/DD/YYYY') || ''
      : '';
    let valuePart = '';
    if (startStr && endStr && startStr !== endStr) {
      valuePart = `${startStr} - ${endStr}`;
    } else {
      valuePart = startStr || endStr || '';
    }
    return [
      {
        label: `${filterLabel}: ${valuePart}`,
        value: 'createdDate',
        enableDelete: true,
      },
    ];
  }, [createdDateFilter, t]);

  const allChips = useMemo(
    () => ({
      brand: brandChips.brand,
      leadOrigin: leadOriginChips,
      createdDate: createdDateChips,
    }),
    [brandChips.brand, createdDateChips, leadOriginChips],
  );

  const removeBrandChip = React.useCallback((chipValue: unknown) => {
    const removeValue = String(chipValue || '').toLowerCase();
    setBrandFilterApplied(prev => prev.filter(item => String(item.value || '').toLowerCase() !== removeValue));
    setBrandFilterDraft(prev => prev.filter(item => String(item.value || '').toLowerCase() !== removeValue));
  }, []);

  const clearAllFilters = React.useCallback(() => {
    setBrandFilterDraft([]);
    setBrandFilterApplied([]);
    setCreatedDateFilter(null);
    setLeadOriginInput('');
    setLeadOriginApplied('');
  }, []);

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        className={classes.tabRoot}
        textColor="primary"
        indicatorColor="primary"
      >
        {tabs.map(tab => (
          <Tab key={tab.value} label={tab.label} value={tab.value} className={classes.tabCell} />
        ))}
      </Tabs>

      <div className={classes.gridSection}>
        <div className={classes.filtersRow}>
          <div className={classes.filterCell}>
            <TypeAheadDropdown
              filterName={t('candidateEngagement.filters.brand')}
              name="candidateProfileLeadsBrand"
              isMultiSelect
              isSelectAll
              options={brandDropdownOptions}
              value={brandFilterDraft}
              onChange={setBrandFilterDraft}
              apply={() => setBrandFilterApplied(brandFilterDraft)}
            />
          </div>
          <div className={classes.filterCell}>
            <TextField
              className={classes.leadOriginField}
              size="small"
              fullWidth
              variant="outlined"
              placeholder={t('candidateEngagement.filters.leadOrigin')}
              value={leadOriginInput}
              onChange={e => setLeadOriginInput(e.target.value)}
              onBlur={commitLeadOriginFilter}
              name="candidateProfileLeadsLeadOrigin"
              inputProps={{ 'aria-label': t('candidateEngagement.filters.leadOrigin') }}
              InputProps={{
                sx: {
                  color: leadOriginInput.trim() ? '#555555' : '#C0C0C0',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#000000',
                  },
                },
              }}
            />
          </div>
          <div className={classes.filterCell}>
            <DateRangePicker
              variant="outlined"
              single
              trailingIconShow
              secondaryLabel={t('candidateEngagement.filters.inquiryCreatedDate')}
              initialDateRange={createdDateFilter || undefined}
              onApply={value => setCreatedDateFilter(value?.startDate || value?.endDate ? value : null)}
              applyOnClickAway
              clickAway={false}
              disablePortal
              useMaxWidth
              maxWidth={210}
            />
          </div>
          <div className={classes.filterActionButtons}>
            <SearchPageIcons resetlbutton onResetForm={clearAllFilters} />
          </div>
        </div>

        {(brandFilterApplied.length > 0 ||
          leadOriginChips.length > 0 ||
          createdDateChips.length > 0) && (
          <div className={classes.chipsRow}>
            <ChipsContainer
              chips={allChips}
              isCollapse
              maxHeight={38}
              onClick={() => {
                return;
              }}
              onDelete={(field, chip) => {
                if (field === 'leadOrigin') {
                  setLeadOriginInput('');
                  setLeadOriginApplied('');
                  return;
                }
                if (field === 'createdDate') {
                  setCreatedDateFilter(null);
                  return;
                }
                removeBrandChip(chip?.value);
              }}
            />
          </div>
        )}

        <div className={classes.tableWrap}>
          {!userPreference?.loaded || displayColumns.length === 0 || !gridInitialState ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <CircularProgress />
            </div>
          ) : (
            <XGrid
              apiRef={gridApiRef}
              initialState={gridInitialState}
              columnVisibilityModel={columnVisibilityModel}
              defaultColumnVisibilityModel={defaultColumnVisibilityModel}
              rows={apiRows}
              columns={displayColumns}
              defaultColumns={defaultColumns}
              gridTag={leadsGridTag}
              title={tabTitle}
              loading={isLoading}
              getRowId={row => row.id}
              disableSelectionOnClick
              density="compact"
              sortingMode="server"
              defaultRowsPerPage={25}
              rowsPerPageOptions={[25, 50, 100]}
              onSortModelChange={model => {
                const next = model?.[0];
                setSortColumn(next?.field || 'createdDate');
                setSortDirection(next?.sort === 'asc' ? 'ASC' : 'DESC');
              }}
              onColumnResizeStop={_columns => {
                savePreferences({
                  columns: _columns,
                  visibilityModel: cloneDeep(columnVisibilityModel ?? defaultColumnVisibilityModel),
                });
              }}
              onColumnOrderChange={(_params, _event, _details, _cols) => {
                savePreferences({
                  columns: _cols,
                  visibilityModel: cloneDeep(columnVisibilityModel ?? defaultColumnVisibilityModel),
                });
              }}
              onColumnVisibilityModelChange={model => {
                dispatch(gridStateActions.setColumnVisibilityModel(model));
                savePreferences({
                  columns: cloneDeep(gridApiRef.current?.getAllColumns() ?? displayColumns),
                  visibilityModel: cloneDeep(model),
                });
                const updatedColumns = cloneDeep(displayColumns).map(column => ({
                  ...column,
                  hide: model[column.field] === false,
                }));
                if (!isEqual(updatedColumns, displayColumns)) {
                  setDisplayColumns(updatedColumns);
                }
              }}
              toolbar
              toolbarProps={{
                columnSelector: true,
                refreshButton: true,
                refreshGrid: () => {
                  setReloadToken(prev => prev + 1);
                },
                resetColumns: () => {
                  determineGridColumns(true);
                  dispatch(gridStateActions.setColumnVisibilityModel(defaultColumnVisibilityModel));
                  dispatch(
                    userDevicePreferenceActions.saveUserGridPreferenceAction({
                      id: leadsGridTag,
                      value: {
                        ...(gridPreferences?.value || {}),
                        columns: new GridColumns(defaultColumns as XGridColumns).columns,
                        columnVisibilityModel: defaultColumnVisibilityModel,
                      },
                    }),
                  );
                  dispatch(
                    globalActions.setSnackBar({
                      message: t('global.xgrid.columnsResetMessage'),
                      severity: 'success',
                    }),
                  );
                  setGridInitialState({
                    columns: { columnVisibilityModel: defaultColumnVisibilityModel },
                  });
                },
              }}
            />
          )}
        </div>
      </div>
    </Box>
  );
};
